import { useState } from "react";
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CloudIcon,
    GlobeEuropeAfricaIcon,
    LockClosedIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const TRUSTED_LOGOS = [
    { src: "/auth/logos/kixx.svg", alt: "Kixx Concept" },
    { src: "/auth/logos/flexport.svg", alt: "Flexport" },
    { src: "/auth/logos/replicable.svg", alt: "Replicable" },
    { src: "/auth/logos/hyred.svg", alt: "Hyred" },
    { src: "/auth/logos/buroz.png", alt: "Buro Z" },
    { src: "/auth/logos/climatools.avif", alt: "Climatools" },
];

// Enterprise-grade security, from the platform overview page.
const SECURITY_FEATURES = [
    { Icon: GlobeEuropeAfricaIcon, label: "EU data residency" },
    { Icon: ShieldCheckIcon, label: "GDPR compliant" },
    { Icon: CloudIcon, label: "Built on AWS" },
    { Icon: LockClosedIcon, label: "AES-256 + TLS 1.2+ encryption" },
];

// Customer testimonials, from the Testimonials CMS collection.
const TESTIMONIALS = [
    {
        quote: "Seeing Spectacle in action was pretty eye-opening. For the first time we could actually see which people at companies were engaging with our content and tools, instead of just staring at anonymous metrics. It already feels like we’ll have a much clearer picture of what’s working in our marketing and sales funnel.",
        name: "Alex Nederlof",
        title: "VP of Engineering",
        photo: "/auth/alex-nederlof.jpeg",
    },
    {
        quote: "Spectacle is exactly what we needed. We can finally tie trials and Chargebee revenue back to the right channels and keywords, straight into HubSpot. It’s the practical, affordable alternative to the big tools — and it just fits how we work.",
        name: "Charles de Poorter",
        title: "Head of Growth",
        photo: "/auth/charles-de-poorter.jpeg",
    },
    {
        quote: "Spectacle delivers on its promise — it surfaces insights we simply can’t get from GA4, Meta, or Pipedrive alone. It gives us a much clearer picture of where a lead actually comes from, which helps us allocate budget to the right channels. The team thinks along with us and implements improvements directly in the platform.",
        name: "Koen van de Poel",
        title: "Founder",
        photo: "/auth/koen-van-de-poel.jpeg",
    },
];

// Fades the node-logo strip out on both sides so it reads as spanning the
// full width and running off the edges.
const EDGE_FADE =
    "linear-gradient(to right, transparent, black 12%, black 88%, transparent)";

function SecurityFeatures() {
    return (
        <div className="flex flex-col gap-4">
            <span className="font-brand-mono text-xs uppercase tracking-wide text-brand-beige/60">
                Enterprise-grade security
            </span>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {SECURITY_FEATURES.map(({ Icon, label }) => (
                    <div
                        key={label}
                        className="flex items-center gap-2.5 text-sm text-brand-beige/80"
                    >
                        <Icon className="h-5 w-5 shrink-0 text-brand-beige/60" />
                        <span>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Sign-up right column: a testimonial carousel (arrows cycle through the CMS
 * testimonials), enterprise-security highlights, and a full-width customer-logo
 * strip that fades out on both sides. Logos are normalised to white silhouettes
 * so the mixed source marks read consistently on the purple panel.
 */
export function SignupTestimonialPanel() {
    const [index, setIndex] = useState(0);
    // `index` is always kept in range by `step`, so this is safe.
    const testimonial = TESTIMONIALS[index]!;

    const step = (delta: number) =>
        setIndex(
            (i) => (i + delta + TESTIMONIALS.length) % TESTIMONIALS.length,
        );

    return (
        <div className="relative hidden w-1/2 flex-col overflow-hidden bg-brand-purple text-white lg:flex">
            <div className="flex flex-1 flex-col px-16 pt-16 xl:px-24 xl:pt-24">
                <figure className="flex flex-1 flex-col justify-center gap-8">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => step(-1)}
                            aria-label="Previous testimonial"
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 text-white/80 transition-colors hover:bg-white/10"
                        >
                            <ArrowLeftIcon className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => step(1)}
                            aria-label="Next testimonial"
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 text-white/80 transition-colors hover:bg-white/10"
                        >
                            <ArrowRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <blockquote className="font-display text-2xl leading-snug">
                        “{testimonial.quote}”
                    </blockquote>
                    <figcaption className="flex items-center gap-4">
                        <img
                            src={testimonial.photo}
                            alt={testimonial.name}
                            className="h-12 w-12 rounded object-cover"
                        />
                        <div className="flex flex-col">
                            <span className="text-sm">{testimonial.name}</span>
                            <span className="text-sm text-brand-beige/70">
                                {testimonial.title}
                            </span>
                        </div>
                    </figcaption>
                </figure>

                <div className="pb-32 pt-4">
                    <SecurityFeatures />
                </div>
            </div>

            <div className="pb-12">
                <div
                    className="flex items-center justify-between gap-10 px-6"
                    style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}
                >
                    {TRUSTED_LOGOS.map((logo) => (
                        <img
                            key={logo.src}
                            src={logo.src}
                            alt={logo.alt}
                            className="h-7 w-auto shrink-0 object-contain opacity-60 brightness-0 invert"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * Sign-in (and other flow) right column: showcases the Audience Hub feature
 * with its looping product video.
 */
export function LoginShowcasePanel() {
    return (
        <div className="hidden w-1/2 flex-col justify-center overflow-hidden bg-brand-beige p-16 lg:flex xl:p-24">
            <div className="flex max-w-lg flex-col gap-6">
                <span className="font-brand-mono text-xs uppercase tracking-wide text-brand-pink">
                    Audience Hub
                </span>
                <h2 className="font-display text-3xl leading-tight text-brand-purple">
                    Push your highest-intent segments straight to Meta and
                    Google.
                </h2>
                <p className="text-base text-brand-grey-warm">
                    Your ad platforms target demographics. Your data knows
                    intent. Audience Hub keeps Spectacle’s highest-value
                    segments live in Meta and Google — so retargeting reaches
                    the right people, exclusions skip real customers, and
                    lookalikes seed from revenue, not noise.
                </p>
                <div className="mt-2 overflow-hidden rounded-xl border border-brand-purple/10 bg-white shadow-sm">
                    <video
                        className="h-full w-full"
                        autoPlay
                        loop
                        muted
                        playsInline
                        poster="/auth/audience-hub-poster.jpg"
                    >
                        <source src="/auth/audience-hub.mp4" type="video/mp4" />
                    </video>
                </div>
            </div>
        </div>
    );
}
